import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Agent, AGENT_PERMISSIONS, channelLabel, Conversation, ConversationMessage, fileSizeLabel, MessageAttachment, playbooksOf, toolsOf } from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';

/** A USER message that came in as a voice recording: its text is the transcript. */
export function isTranscript(m: Pick<ConversationMessage, 'role' | 'attachments'>): boolean {
  return m.role === 'USER' && (m.attachments || []).some((a) => a.kind === 'AUDIO');
}

@Component({
  selector: 'app-ai-conversation-detail-page',
  templateUrl: './conversation-detail-page.component.html',
  styleUrls: ['../../ai-agents.shared.css', './conversation-detail-page.component.css'],
})
export class ConversationDetailPageComponent implements OnInit, OnDestroy {
  readonly P = AGENT_PERMISSIONS;
  readonly isTranscript = isTranscript;
  /** Contract v1.4: "Playbook: <title>" chips of an assistant message. */
  readonly playbooksOf = playbooksOf;
  /** Contract v1.5: "Tool: <tool>" chips of an assistant message (from toolsUsed). */
  readonly toolsOf = toolsOf;
  /** Contract v1.6: TRIGGER is shown as "Automation". */
  readonly channelLabel = channelLabel;

  conversation: Conversation | null = null;
  messages: ConversationMessage[] = [];
  agent: Agent | null = null;
  error = '';

  /** Object URLs of the loaded attachments, by attachmentId (revoked on reload and on destroy). */
  attachmentUrls: Record<string, string> = {};
  attachmentErrors: Record<string, string> = {};
  /** Contract v1.3: documents being fetched for a download. */
  downloading: Record<string, boolean> = {};
  private attachmentSubs: Subscription[] = [];

  constructor(
    private api: AiAgentsApiService,
    public perms: AgentPermissionsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((p) => {
      const id = p.get('id');
      if (id) this.load(id);
    });
  }

  ngOnDestroy(): void {
    this.releaseAttachments();
  }

  get totals(): { input: number; output: number; failed: number } {
    return this.messages.reduce(
      (t, m) => ({
        input: t.input + (m.inputTokens || 0),
        output: t.output + (m.outputTokens || 0),
        failed: t.failed + (m.failed ? 1 : 0),
      }),
      { input: 0, output: 0, failed: 0 }
    );
  }

  load(id: string): void {
    this.error = '';
    forkJoin([this.api.getConversation(id), this.api.getConversationMessages(id)]).subscribe({
      next: ([c, msgs]) => {
        this.conversation = c;
        this.messages = msgs || [];
        this.loadAttachments(this.messages);
        if (this.perms.has(this.P.agentGet)) {
          this.api
            .getAgent(c.agentId)
            .pipe(catchError(() => of(null)))
            .subscribe((a) => (this.agent = a));
        }
      },
      error: () => (this.error = 'The conversation could not be loaded.'),
    });
  }

  attachmentsOf(m: ConversationMessage): MessageAttachment[] {
    return m.attachments || [];
  }

  /**
   * Loads every image and recording as a blob (the request carries the admin token) and keeps an object URL.
   * Documents (v1.3) are fetched only when they are downloaded.
   */
  loadAttachments(messages: ConversationMessage[]): void {
    this.releaseAttachments();
    for (const m of messages) {
      for (const a of m.attachments || []) {
        if (!a?.attachmentId || a.kind === 'DOCUMENT' || this.attachmentUrls[a.attachmentId]) continue;
        const sub = this.api.getSystemAttachment(a.attachmentId).subscribe({
          next: (blob) => {
            const typed = blob && !blob.type && a.contentType ? new Blob([blob], { type: a.contentType }) : blob;
            this.attachmentUrls = { ...this.attachmentUrls, [a.attachmentId]: URL.createObjectURL(typed) };
          },
          error: (err) => {
            this.attachmentErrors = {
              ...this.attachmentErrors,
              [a.attachmentId]: err?.status === 404 ? 'The file is no longer stored.' : 'The file could not be loaded.',
            };
          },
        });
        this.attachmentSubs.push(sub);
      }
    }
  }

  documentName(a: MessageAttachment): string {
    return a.fileName || 'Document';
  }

  /** "120 KB · 3 pages" */
  documentMeta(a: MessageAttachment): string {
    const parts: string[] = [];
    if (typeof a.size === 'number') parts.push(fileSizeLabel(a.size));
    if (a.pages) parts.push(a.pages === 1 ? '1 page' : a.pages + ' pages');
    return parts.join(' · ');
  }

  /** Contract v1.3: downloads a document of the conversation under its own name. */
  downloadDocument(a: MessageAttachment): void {
    if (this.downloading[a.attachmentId]) return;
    this.downloading = { ...this.downloading, [a.attachmentId]: true };
    const sub = this.api.getSystemAttachment(a.attachmentId).subscribe({
      next: (blob) => {
        this.downloading = { ...this.downloading, [a.attachmentId]: false };
        const typed = blob && !blob.type && a.contentType ? new Blob([blob], { type: a.contentType }) : blob;
        const url = URL.createObjectURL(typed);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.documentName(a);
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      },
      error: (err) => {
        this.downloading = { ...this.downloading, [a.attachmentId]: false };
        this.attachmentErrors = {
          ...this.attachmentErrors,
          [a.attachmentId]: err?.status === 404 ? 'The file is no longer stored.' : 'The file could not be loaded.',
        };
      },
    });
    this.attachmentSubs.push(sub);
  }

  private releaseAttachments(): void {
    this.attachmentSubs.forEach((s) => s.unsubscribe());
    this.attachmentSubs = [];
    Object.values(this.attachmentUrls).forEach((url) => URL.revokeObjectURL(url));
    this.attachmentUrls = {};
    this.attachmentErrors = {};
  }

  back(): void {
    this.router.navigate(['/ai-agents/conversations'], {
      queryParams: { projectId: this.conversation?.projectId || null, agentId: this.conversation?.agentId || null },
    });
  }

  openAgent(): void {
    if (this.conversation) {
      this.router.navigate(['/ai-agents/agents', this.conversation.agentId]);
    }
  }
}
